// Apolo IDE — shared formatters + status tones for the agents/workflows
// surfaces.
//
// Two surfaces consume this:
//   - AgentsWorkflowsLane  (macro: cards of every active workflow, in the
//                           Command Center — visão de todas as sessões)
//   - SessionAgentsIndicator (micro: the green dot + agents of ONE session,
//                             in the Code conversation toolbar)
//
// Identity comes from COLOR + TEXT, never icons — mirroring the reference
// panel's per-agent columns: Agente · Tokens · Ferramentas · Horário.

export const RUNNING_STATUSES = new Set(['running', 'pending']);
export const TERMINAL_STATUSES = new Set([
  'completed',
  'failed',
  'failed_timeout',
  'cancelled',
]);

// Status → dot/text classes + pt-BR label. Mirrors SubagentTracker so the
// macro and micro surfaces agree on what each color means.
export function statusTone(status) {
  switch (status) {
    case 'running':
      return { dot: 'bg-apolo-accent', text: 'text-apolo-accent', label: 'rodando' };
    case 'completed':
      return { dot: 'bg-apolo-success', text: 'text-apolo-success', label: 'concluído' };
    case 'failed':
    case 'failed_timeout':
      return { dot: 'bg-apolo-error', text: 'text-apolo-error', label: 'falhou' };
    case 'cancelled':
      return { dot: 'bg-apolo-warning', text: 'text-apolo-warning', label: 'cancelado' };
    case 'pending':
      return { dot: 'bg-apolo-textTertiary', text: 'text-apolo-textTertiary', label: 'na fila' };
    default:
      return {
        dot: 'bg-apolo-textTertiary',
        text: 'text-apolo-textTertiary',
        label: status || 'idle',
      };
  }
}

export function fmtTokens(n) {
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 0) return '0';
  if (n < 1000) return String(Math.round(n));
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

// ms → "1m 57s" / "57s" / "2h 3m". Matches the reference "Horário" column.
export function fmtDuration(ms) {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return '—';
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// Live elapsed since a dispatch started (or its full span once terminal).
export function elapsedSince(startIso, endIso) {
  if (!startIso) return null;
  const start = new Date(startIso).getTime();
  if (!Number.isFinite(start)) return null;
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  if (!Number.isFinite(end)) return null;
  return Math.max(0, end - start);
}

export function goalOf(run) {
  if (run && run.spec_json && typeof run.spec_json === 'object' && run.spec_json.goal) {
    return String(run.spec_json.goal);
  }
  return '(sem objetivo)';
}

export function toolsCount(run) {
  const t = run && run.spec_json && run.spec_json.tools;
  return Array.isArray(t) ? t.length : 0;
}

// Normalize a raw subagent_run row into the slim shape the UI renders.
export function agentFromRun(run) {
  if (!run || typeof run !== 'object') {
    return { id: null, goal: '(sem objetivo)', model: 'unknown', status: 'pending', tokens_used: 0, tools_count: 0, cost_usd: 0, duration_ms: 0 };
  }
  return {
    id: run.id,
    goal: goalOf(run),
    model: (run.spec_json && run.spec_json.model) || 'unknown',
    status: run.status || 'pending',
    tokens_used: Number(run.tokens_used || 0),
    tools_count: toolsCount(run),
    cost_usd: Number(run.cost_usd || 0),
    duration_ms: Number(run.duration_ms || 0),
  };
}

// Human title for a dispatch: the decompose/compare summary, else a short id.
export function dispatchTitle(dispatch) {
  if (!dispatch) return 'Workflow';
  const s = dispatch.spec_json;
  if (s && typeof s === 'object' && (s.summary || s.prompt)) {
    return String(s.summary || s.prompt);
  }
  const shortId = String(dispatch.id || '').slice(0, 8);
  return dispatch.kind ? `${dispatch.kind} ${shortId}`.trim() : shortId || 'Workflow';
}

// Achata os subagentes de workflows (+ recent) em uma lista única, dedup por id,
// preservando ordem. Usado pela seção "Subagentes" do rail do Code chat (Fase 2).
export function collectSubagents(workflows, recent) {
  const out = [];
  const seen = new Set();
  const sources = [...(workflows || [])];
  if (recent) sources.push(recent);
  for (const w of sources) {
    for (const a of (w && w.agents) || []) {
      const key = a.id || a.goal;
      if (key && seen.has(key)) continue;
      if (key) seen.add(key);
      out.push(a);
    }
  }
  return out;
}

// completed+failed+cancelled / total → 0..100.
export function progressPercent(progress) {
  const total = (progress && progress.total) || 0;
  if (total <= 0) return 0;
  const done = (progress.completed || 0) + (progress.failed || 0) + (progress.cancelled || 0);
  return Math.min(100, Math.round((done / total) * 100));
}

// pos-s5 — flatten the two live subagent sources (in-turn S5 runs explore/bash/
// browser via useSubagentRuns + dispatched Team/Hermes agents via
// collectSubagents) into the shape the right-rail SubagentsTab renders:
// { kind, name, summary, status, model, tokens, cost }. Pure — unit-tested apart
// from the hooks. (T1) `model`/`tokens` are carried as DISTINCT fields (no longer
// collapsed into `summary`) so the lane can show a Cursor-style per-agent model
// badge — "each subagent using the best model for the task".
const PANEL_KIND_LABELS = { explore: 'Explore', bash: 'Bash', browser: 'Browser' };
const normModel = (m) => (m && m !== 'unknown' ? String(m) : '');
export function panelSubagents(s5runs, dispatched) {
  const clip = (s, n) => (s ? String(s).split('\n')[0].slice(0, n) : '');
  const fromS5 = (Array.isArray(s5runs) ? s5runs : []).map((r) => ({
    kind: r.kind || 'explore',
    name: PANEL_KIND_LABELS[r.kind] || r.kind || 'Subagent',
    summary: clip(r.input, 60) || clip(r.summary, 60) || '—',
    status: r.status || 'pending',
    // S5 runs (explore/bash/browser) inherit the session model and aren't token-
    // metered per-run, so these are usually empty — the badge just hides itself.
    model: normModel(r.model),
    tokens: Number(r.tokens_used || r.tokens || 0),
    cost: Number(r.cost_usd || 0),
  }));
  const fromDispatch = (Array.isArray(dispatched) ? dispatched : []).map((a) => ({
    kind: a.kind || 'explore',
    name: a.goal || 'Subagent',
    summary: '—', // model moved to its own badge; goal is the row title
    status: a.status || 'pending',
    model: normModel(a.model),
    tokens: Number(a.tokens_used || a.tokens || 0),
    cost: Number(a.cost_usd || 0),
  }));
  return [...fromS5, ...fromDispatch];
}
